import { getDependencyClass } from '../../lib/dependencies.js';
import {
    isPayingSubscriber,
    getPlanCodes,
    getNewsletterSubscriptions,
    isNewsletterSubscriber,
} from './util.js';

getDependencyClass('phoenix-page').then((pageClass) => {
    class Pushly extends pageClass.PhoenixElement {
        constructor() {
            super();
        }

        // This implemenation loads other pushly methods we want to call
        // from the client-side to extract new user data and send it to pushly
        connectedCallback() {
            super.connectedCallback();
            this.canRender()
                .then((canRender) => {
                    if (canRender) {
                        this.render();
                    }
                })
                .catch((err) => {
                    // eslint-disable-next-line no-console -- neccesary error handling
                    console.error('Could not render Pushly due to the following error: ', err);
                });
        }

        render() {
            // load PushlySDK
            window.PushlySDK = window.PushlySDK || [];

            function pushly() {
                window.PushlySDK.push(arguments);
            }

            pushly('load', { domainKey: this.config.domainKey, sw: '/sw.js' });

            // add the pushly service worker script to the page
            const scriptSrc = 'https://cdn.p-n.io/pushly-sdk.min.js?domain_key=' + this.config.domainKey;
            pageClass.util.addScriptResourceOnce(scriptSrc);

            // the on_ready event is fired when the pushly sdk is ready to be used
            // this ensures we only call the other pushly methods
            // after the PushlySDK has loaded
            pushly('on_ready', () => {
                // retrieve user info from the RGIS cookie and sitekeywork
                // and then send relevant user info to pushly
                this.getUserInfo(this.RGISCookieValue, this.config.siteKeyword).then((userInfo) => {
                    // if the users susbscriptions length is greater than 0, it indicates the user is a pro
                    // subscriber as this subscriptions list returns active paying subscriptions for a site
                    // which is currently only theStreetPro

                    // get the logged in user status by using window.phxGetLoggedInStatus
                    let userStatus = window.phxGetLoggedInStatus();
                    let isLoggedInUser = userStatus?.['type'] === 'rgis';

                    // Here there is two seperate method calls, one for if the user is a
                    // paying subscriber which we check on every logged in subscriber
                    // and the other for if the user is a logged in user which we check every login
                    // action whether the user is logged in or not
                    // the reason being is logged out users who have an external id are
                    // being untracked by pushly as paying subscribers corresponding to their
                    // external id, so we need to track them as paying subsribers only as logged
                    // in users
                    if (isLoggedInUser) {
                        pushly('external_id', userStatus['id']);
                        pushly('profile', {
                            is_paying_subscriber: isPayingSubscriber(userInfo),
                            is_logged_in_user: isLoggedInUser,
                            sub_planCodes: getPlanCodes(userInfo),
                        });

                        this.getUserSubscriptionsPreferences(
                            this.RGISCookieValue,
                            this.config.siteKeyword
                        ).then((userPreferences) => {
                            const isNewsletterSub = isNewsletterSubscriber(userPreferences);
                            pushly('profile', {
                                is_newsletter_subscriber: isNewsletterSub,
                                newsletter_subs_list: getNewsletterSubscriptions(userPreferences),
                            });
                        });
                    }

                    // checks if a user is logged in or out on every login action and page load
                    pushly('profile', {
                        is_logged_in_user: isLoggedInUser,
                    });
                });
            });
        }

        /**
         * This function gets the user info related to a user's
         * profile by passing in the RGIS cookie value and
         * siteKeyword.
         * This function returns a JSON object with
         * the user's id and list of active paying subscriptions
         * for the siteKeyword passed in.
         *
         * @param {string} rgisCookieVal - The RGIS cookie value.
         * @param {string} siteKeyword - The siteKeyword value used to cross
         * reference a user's subscriptions on a specific site.
         * @returns {Promise<Object>} A promise that resolves to
         * a JSON object containing the user's id and list of active paying subscriptions.
         */
        async getUserInfo(rgisCookieVal, siteKeyword) {
            if (!rgisCookieVal) {
                return {};
            }

            try {
                const response = await fetch(
                    `/.api/memberships/comments-profile?siteKeyword=${siteKeyword}&rgis=${encodeURIComponent(
                        rgisCookieVal
                    )}`
                );
                if (response.status !== 200) {
                    return;
                }
                return await response.json();
            } catch (err) {
                // eslint-disable-next-line no-console -- necessary error handling
                console.error('Error fetching user info', err);
                return;
            }
        }

        /**
         * This function gets the users subscriptions in taiga
         * This function returns a JSON object with
         * the user's subscriptions such as
         * newsletters, tickers, and author subnscriptions
         * for the siteKeyword passed in.
         *
         * @param {string} rgisCookieVal - The RGIS cookie value.
         * @param {string} siteKeyword - The siteKeyword value used to cross
         * reference a user's subscriptions on a specific site.
         * @returns {Promise<Object>} A promise that resolves to
         * a JSON object containing the user's subscriptions.
         */

        async getUserSubscriptionsPreferences(rgisCookieVal, siteKeyword) {
            if (!rgisCookieVal) {
                return;
            }

            try {
                const response = await fetch(
                    `/.api/memberships/subscription-user-preferences?siteKeyword=${siteKeyword}&rgis=
                    ${encodeURIComponent(rgisCookieVal)}`
                );

                if (response.status !== 200) {
                    return;
                }

                return await response.json();
            } catch (err) {
                // eslint-disable-next-line no-console -- necessary error handling
                console.error('Error fetching newsletter subscriptions', err);
                return;
            }
        }
    }

    window.customElements.define('phoenix-pushly', Pushly);
});
